import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {connect} from 'react-redux';
import {Alert, Divider, Space} from 'antd';
import {StyledApproveExpenseModal, StyledApproveExpenseModalDescription, StyledApproveExpenseModalApprovalsSpace} from './StyledApproveExpenseModal';
import SpinSmall from '../../../../../SpinSmall';
import UserReview from '../../../../../UserReview';
import {LockIcon} from '../../../../../../icons';
import {dateHelpers, expensesHelpers} from '../../../../../../utils/helpers';

const defaultError = {
  message: null,
  open: false
};

const ReviewsList = ({ reviews = [], ...rest } = {}) => (
  <StyledApproveExpenseModalApprovalsSpace direction='vertical' size={0} {...rest}>
    {reviews.map((review, idx) => {
      const isApproved = review?.is_approved;
      const date = dateHelpers.getDateFromISO(review.approval_date, 'DD MMM YYYY, HH:mm');
      const status = isApproved === true ? 'approved' : isApproved === false ? 'rejected' : 'default';

      return (
        <React.Fragment key={`${review.id}-${idx}`}>
          <UserReview
            date={date}
            message={review.comment}
            reviewer={review.approver}
            status={status}
          />
          {idx < reviews.length - 1 && <Divider style={{ margin: '10px 0', width: 32 }} />}
        </React.Fragment>
      )})}
  </StyledApproveExpenseModalApprovalsSpace>
);

const ApproveExpenseModal = ({
  dispatch,
  employeeEmail,

  handleOk,
  handleCancel,
  expense,
  loading = false,
  open,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [error, setError] = useState(defaultError);

  const trans = (key) => t(`expenses:modal.approveExpense.${key}`);

  const reviews = useMemo(() => {
    return expensesHelpers.getExpenseApprovalReviews(expense, employeeEmail);
  }, [expense?.approval, employeeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAfterClose = () => {
    if (error.open) setError(defaultError);
  }

  const handleSubmit = () => {
    if (handleOk) {
      handleOk?.({
        errorCallback: (response) => {
          const message = response?.data?.message;
          if (message) {
            setError({ ...error, message, open: true });
          }
        }
      });
    }
  }

  return (
    <StyledApproveExpenseModal
      afterClose={handleAfterClose}
      cancelButtonProps={{size: 'middle'}}
      forceRender
      okButtonProps={{
        disabled: loading,
        size: 'middle',
        type: 'primary',
      }}
      okText={t('confirm')}
      onCancel={handleCancel}
      onOk={handleSubmit}
      open={open}
      title={trans('title')}
      width={400}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Space size='middle' direction='vertical'>
          <Space size='small' direction='vertical' align='center'>
            <StyledApproveExpenseModalDescription>
              <LockIcon />
            </StyledApproveExpenseModalDescription>
            <StyledApproveExpenseModalDescription>
              {trans(reviews.length ? 'description' : 'descriptionNoReviews')}
            </StyledApproveExpenseModalDescription>
          </Space>
          <ReviewsList reviews={reviews} />
        </Space>
        <Collapse isOpened={error.open}>
          <Alert message={error.message} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledApproveExpenseModal>
  );
}

ApproveExpenseModal.propTypes = {
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  expense: PropTypes.object,
  loading: PropTypes.bool,
  open: PropTypes.bool.isRequired,
}

ApproveExpenseModal.ReviewsList = ReviewsList;

const mapStateToProps = ({ user: { employee: { email: employeeEmail } } }) => ({
  employeeEmail,
});

export default connect(mapStateToProps, null)(ApproveExpenseModal);
